import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { BackHeader } from "@/components/TopBar";
import { Spinner } from "@/components/common";
import { CompatibilityBreakdown } from "@/components/CompatibilityBreakdown";
import { TrustBadges } from "@/components/TrustBadges";
import { CompatibilityRing } from "@/components/CompatibilityRing";
import { SaveButton } from "@/components/SaveButton";
import { Button } from "@/components/ui/button";
import { fmtRent, fmtDate, LABELS } from "@/lib/constants";
import { toast } from "sonner";
import { MapPin, Wallet, BedDouble, Sofa, Calendar, Check, MessageCircle, Heart } from "lucide-react";

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [l, setL] = useState(null);
  const [idx, setIdx] = useState(0);
  const [savedIds, setSavedIds] = useState([]);

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => setL(r.data)).catch(() => toast.error("Listing not found"));
    api.get("/saved/ids").then((r) => setSavedIds(r.data.ids)).catch(() => {});
  }, [id]);

  if (!l) return <div><BackHeader title="Listing" /><Spinner /></div>;

  const images = (l.images?.length ? l.images : ["https://images.unsplash.com/photo-1662454419716-c4c504728811?w=800"]).map(resolveImg);

  const interest = async () => {
    try { await api.post("/interests", { target_type: "listing", target_id: l.listing_id }); toast.success("Interest sent to the host!"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send interest"); }
  };
  const message = async () => {
    try {
      const { data } = await api.post("/conversations", { other_user_id: l.owner_id, listing_id: l.listing_id });
      navigate(`/chat/${data.conversation_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start chat"); }
  };

  return (
    <div className="pb-28">
      <BackHeader title={l.locality} right={<SaveButton itemType="listing" itemId={l.listing_id} savedIds={savedIds} onChange={(i, on) => setSavedIds((s) => on ? [...s, i] : s.filter((x) => x !== i))} />} />

      <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
        <img src={images[idx]} alt="" className="h-full w-full object-cover" />
        <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
          {images.map((_, i) => <button key={i} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/60"}`} />)}
        </div>
        {l.compatibility && <div className="absolute right-3 top-3 rounded-2xl bg-white p-1 shadow-soft"><CompatibilityRing score={l.compatibility.score} size={52} stroke={5} /></div>}
      </div>
      {images.length > 1 && (
        <div className="no-scrollbar flex gap-2 overflow-x-auto px-4 py-3">
          {images.map((im, i) => <img key={i} src={im} onClick={() => setIdx(i)} alt="" className={`h-16 w-20 shrink-0 cursor-pointer rounded-xl object-cover ${i === idx ? "ring-2 ring-brand" : ""}`} />)}
        </div>
      )}

      <div className="space-y-6 px-4 pt-4">
        <div>
          <div className="flex items-start justify-between">
            <div>
              <span className="rounded-full bg-ink px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">{l.room_type === "shared" ? "Shared room" : "Private room"}</span>
              <h1 className="mt-2 font-heading text-2xl font-extrabold text-ink">{l.title}</h1>
              <p className="flex items-center gap-1 text-ink-soft"><MapPin className="h-4 w-4 text-brand" /> {[l.area, l.locality, l.city].filter(Boolean).join(", ")}</p>
            </div>
          </div>
          <p className="mt-3 font-heading text-3xl font-extrabold text-ink">{fmtRent(l.rent)}<span className="text-base font-medium text-ink-soft">/month</span> <span className="text-sm font-semibold text-ink-soft">· rent only</span></p>
        </div>

        <div className="rounded-3xl bg-ink p-5 text-white" data-testid="cost-breakdown">
          <p className="text-xs font-bold uppercase tracking-widest text-white/60">Monthly cost breakdown</p>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-white/80">Rent</span><span className="font-semibold">{fmtRent(l.rent)}</span></div>
            <div className="flex justify-between"><span className="text-white/80">Shared expenses</span><span className="font-semibold">{fmtRent(l.expense_total || 0)}</span></div>
            <div className="mt-2 flex justify-between border-t border-white/15 pt-2"><span className="font-bold">Total monthly housing cost</span><span className="font-heading text-xl font-extrabold text-sun">{fmtRent(l.monthly_total != null ? l.monthly_total : (l.rent || 0) + (l.expense_total || 0))}</span></div>
          </div>
          {l.budget_fit && (
            <div data-testid="budget-fit-detail" className={`mt-3 flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ${l.budget_fit.fits ? "bg-mint/15 text-mint" : "bg-sunset/15 text-sunset"}`}>
              <Wallet className="h-4 w-4" />
              {l.budget_fit.fits ? "Fits your total budget" : `Above your budget (cap ${fmtRent(l.budget_fit.cap)})`}
            </div>
          )}
          {(l.expense_total > 0) && (
            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-white/15 pt-3 text-xs text-white/70">
              {l.expense_maid > 0 && <span>Maid: {fmtRent(l.expense_maid)}</span>}
              {l.expense_maintenance > 0 && <span>Maintenance: {fmtRent(l.expense_maintenance)}</span>}
              {l.expense_furniture > 0 && <span>Furniture: {fmtRent(l.expense_furniture)}</span>}
              {l.expense_misc > 0 && <span>Misc: {fmtRent(l.expense_misc)}</span>}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Stat icon={Wallet} label="Deposit" value={fmtRent(l.deposit)} />
          <Stat icon={BedDouble} label="Configuration" value={l.flat_config} />
          <Stat icon={Sofa} label="Furnishing" value={LABELS.room_preference && (l.furnishing || "—")} />
          <Stat icon={Calendar} label="Move-in" value={fmtDate(l.move_in_date)} />
          {l.food_arrangement && <Stat icon={Sofa} label="Cooking" value={LABELS.food_arrangement?.[l.food_arrangement] || l.food_arrangement} />}
          {l.bathrooms ? <Stat icon={BedDouble} label="Bathrooms" value={`${l.bathrooms}${l.attached_bathroom ? " · attached" : ""}`} /> : null}
        </div>

        {l.amenities?.length > 0 && (
          <div>
            <h3 className="mb-2 font-heading text-lg font-bold text-ink">Amenities</h3>
            <div className="flex flex-wrap gap-2">
              {l.amenities.map((a) => <span key={a} className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-ink shadow-soft"><Check className="h-3.5 w-3.5 text-mint" /> {a}</span>)}
            </div>
          </div>
        )}

        {l.description && (
          <div>
            <h3 className="mb-2 font-heading text-lg font-bold text-ink">About this place</h3>
            <p className="leading-relaxed text-ink-soft">{l.description}</p>
            <p className="mt-2 text-xs text-ink-soft">📍 {l.address || "Exact address shared after connecting"}</p>
          </div>
        )}

        {l.ideal_text && (
          <div className="rounded-3xl bg-magenta-light p-4" data-testid="listing-ideal">
            <h3 className="mb-1 font-heading text-lg font-bold text-ink">Their ideal flatmate</h3>
            <p className="leading-relaxed text-ink-soft">{l.ideal_text}</p>
          </div>
        )}

        {l.occupants?.length > 0 && (
          <div>
            <h3 className="mb-1 font-heading text-lg font-bold text-ink">Meet your future flatmates</h3>
            <p className="mb-3 text-sm text-ink-soft">The people already living here.</p>
            <div className="space-y-3">
              {l.occupants.map((o) => (
                <div key={o.user_id} onClick={() => navigate(`/person/${o.user_id}`)} className="flex cursor-pointer items-center gap-3 rounded-3xl bg-white p-3 shadow-soft">
                  <img src={resolveImg(o.profile?.photo_url)} alt="" className="h-14 w-14 rounded-2xl object-cover" />
                  <div className="flex-1">
                    <h4 className="font-heading font-bold text-ink">{o.profile?.first_name}{o.profile?.age ? `, ${o.profile.age}` : ""}</h4>
                    <p className="text-xs text-ink-soft">{o.profile?.occupation}</p>
                    <div className="mt-1"><TrustBadges verification={o.verification} /></div>
                  </div>
                  <CompatibilityRing score={o.compatibility?.score} size={46} stroke={4} />
                </div>
              ))}
            </div>
          </div>
        )}

        {l.compatibility && l.owner && <CompatibilityBreakdown compatibility={l.compatibility} name={l.owner.profile?.first_name} />}
      </div>

      <div className="fixed bottom-0 left-1/2 flex w-full max-w-[480px] -translate-x-1/2 gap-3 border-t border-gray-100 bg-white/95 px-4 py-4 backdrop-blur-xl">
        <Button data-testid="express-interest" onClick={interest} variant="outline" className="h-12 flex-1 rounded-full border-brand font-bold text-brand"><Heart className="mr-1.5 h-4 w-4" /> Interested</Button>
        <Button data-testid="message-host" onClick={message} className="h-12 flex-1 rounded-full bg-brand font-bold text-white shadow-brand hover:bg-brand-dark"><MessageCircle className="mr-1.5 h-4 w-4" /> Message</Button>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white p-3 shadow-soft">
      <div className="flex items-center gap-1.5 text-ink-soft"><Icon className="h-4 w-4" /><span className="text-xs">{label}</span></div>
      <p className="mt-1 font-heading font-bold capitalize text-ink">{value}</p>
    </div>
  );
}
